import React, { Component } from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { Button, Confirm, Grid, Header, Image, Icon, Loader, Table, Label, Select } from "semantic-ui-react";
import PhotoEditingImage from "~/models/PhotoEditing/PhotoEditingImage";
import TimeOfDay from "~/constants/PhotoEditing/TimeOfDay";
import Location from "~/constants/PhotoEditing/Location";
import Link from "~/components/element/Link";
import JobType from "~/constants/PhotoEditing/JobType";

@observer
class ImagesDetails extends Component {
  constructor(props) {
    super(props);

    this.state = {
      openConfirm: false,
      photoEditingImageId: 0,
    };
  }

  onRemoveImageClick(photoEditingImageId) {
    this.setState({ photoEditingImageId, openConfirm: true });
  }

  onCancel() {
    this.setState({ photoEditingImageId: 0, openConfirm: false });
  }

  /**
   * Do remove the image
   */
  onConfirm() {
    const { removeImageCallback } = this.props;
    const { photoEditingImageId } = this.state;

    // close the confirm while we delete the image in the background.
    this.setState({ photoEditingImageId: 0, openConfirm: false });

    return removeImageCallback(photoEditingImageId);
  }

  render() {
    const {
      isSavingImage,
      photoEditingJobTypeId,
      photoEditingImages,
      reviewImagesUrl,
      toggleTimeOfDayCallback,
      toggleLocationCallback,
      batchUpdateImageSettingsCallback,
      editCallback,
    } = this.props;
    const { openConfirm } = this.state;

    return (
      <div className="image-details">
        <Confirm
          open={openConfirm}
          header="Warning"
          content="Image and all its bracketed photos will be deleted. Do you want to continue?"
          onCancel={() => this.onCancel()}
          onConfirm={() => this.onConfirm()}
        />
        {photoEditingJobTypeId === JobType.HD_REAL ? (
          <Grid columns={3}>
            <Grid.Column width={4}>
              <Header size="small">Apply settings to all images</Header>
            </Grid.Column>
            <Grid.Column width={6} textAlign="right">
              <Button.Group size="small" basic>
                <Button onClick={() => batchUpdateImageSettingsCallback({ timeOfDay: TimeOfDay.DAY_TIME })}>
                  <Icon name="sun" />
                  Day Time
                </Button>
                <Button onClick={() => batchUpdateImageSettingsCallback({ timeOfDay: TimeOfDay.NIGHT_TIME })}>
                  <Icon name="moon" />
                  Night Time
                </Button>
              </Button.Group>
            </Grid.Column>
            <Grid.Column width={6} textAlign="right">
              <Button.Group size="small" basic>
                <Button onClick={() => batchUpdateImageSettingsCallback({ location: Location.INTERIOR })}>
                  <Icon name="bed" />
                  Interior
                </Button>
                <Button onClick={() => batchUpdateImageSettingsCallback({ location: Location.EXTERIOR })}>
                  <Icon name="tree" />
                  Exterior
                </Button>
              </Button.Group>
            </Grid.Column>
          </Grid>
        ) : null}
        <Table columns={2}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell colSpan={2} textAlign="center">
                Configuration <Loader active={isSavingImage} inline size="tiny" />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {photoEditingImages.map((photoEditingImage, imageIndex) => (
              <Table.Row key={photoEditingImage.photoEditingImageId}>
                <Table.Cell verticalAlign="middle" textAlign="left" width={4} className="review-actions">
                  <Label>Image # {imageIndex + 1}</Label>
                  <br />
                  <br />
                  {photoEditingJobTypeId === JobType.HD_REAL ? (
                    <>
                      <Select
                        value={photoEditingImage.timeOfDay}
                        onChange={() => toggleTimeOfDayCallback(photoEditingImage.photoEditingImageId)}
                        options={[
                          {
                            value: TimeOfDay.DAY_TIME,
                            text: "Day Time",
                          },
                          {
                            value: TimeOfDay.NIGHT_TIME,
                            text: "Night Time",
                          },
                        ]}
                        style={{ marginBottom: "5px" }}
                      />
                      <Select
                        value={photoEditingImage.location}
                        onChange={() => toggleLocationCallback(photoEditingImage.photoEditingImageId)}
                        options={[
                          {
                            value: Location.INTERIOR,
                            text: "Interior",
                          },
                          {
                            value: Location.EXTERIOR,
                            text: "Exterior",
                          },
                        ]}
                      />
                      <br />
                      <br />
                    </>
                  ) : null}
                  <Button.Group size="small" basic>
                    <Link as={Button} to={reviewImagesUrl} onClick={editCallback}>
                      <Icon name="edit" />
                      Edit&nbsp;
                    </Link>
                    <Button onClick={() => this.onRemoveImageClick(photoEditingImage.photoEditingImageId)}>
                      <Icon name="trash" />
                      Delete
                    </Button>
                  </Button.Group>
                </Table.Cell>
                <Table.Cell verticalAlign="middle" textAlign="right" width={12}>
                  <Image.Group size="small">
                    {photoEditingImage.photoEditingPhotos.map(photoEditingPhoto => (
                      <Image
                        key={photoEditingPhoto.photoEditingPhotoId}
                        bordered
                        rounded
                        src={photoEditingPhoto.thumbnailUrl || photoEditingPhoto.url}
                      />
                    ))}
                  </Image.Group>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    );
  }
}

ImagesDetails.propTypes = {
  isSavingImage: PropTypes.bool.isRequired,
  photoEditingJobTypeId: PropTypes.number.isRequired,
  photoEditingImages: PropTypes.arrayOf(PropTypes.instanceOf(PhotoEditingImage)),
  reviewImagesUrl: PropTypes.string.isRequired,
  batchUpdateImageSettingsCallback: PropTypes.func.isRequired,
  toggleTimeOfDayCallback: PropTypes.func.isRequired,
  toggleLocationCallback: PropTypes.func.isRequired,
  removeImageCallback: PropTypes.func.isRequired,
  // optional, only used when outside react-router scope
  editCallback: PropTypes.func,
};

export default ImagesDetails;
